import React from "react";
import styled from "styled-components";
import { ShieldAlert, ArrowLeft, Home, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UnauthorizedPageProps {
  requiredPermission?: string;
  moduleName?: string;
  redirectPath?: string;
}

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%)";

export const UnauthorizedPage: React.FC<UnauthorizedPageProps> = ({
  requiredPermission,
  moduleName,
  redirectPath = "/dashboard",
}) => {
  const navigate = useNavigate();

  return (
    <Container>
      <Card>
        <Glow aria-hidden />
        <IconWrap>
          <ShieldAlert size={36} color="#c94daa" strokeWidth={1.8} />
        </IconWrap>

        <Badge>
          <Lock size={12} />
          Access Denied
        </Badge>

        <Title>Permission Required</Title>
        <Subtitle>
          You don&apos;t have access to view this section of the studio. Please contact your studio owner or manager to update your role permissions.
        </Subtitle>

        {requiredPermission && (
          <PermissionTag>
            Required key: <code>{requiredPermission}</code>
          </PermissionTag>
        )}

        {moduleName && (
          <PermissionTag>
            Module: <strong>{moduleName}</strong>
          </PermissionTag>
        )}

        <ButtonGroup>
          <BackButton type="button" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
            Go back
          </BackButton>
          <PrimaryButton type="button" onClick={() => navigate(redirectPath)}>
            <Home size={16} />
            Dashboard
          </PrimaryButton>
        </ButtonGroup>
      </Card>
    </Container>
  );
};

export default UnauthorizedPage;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: transparent;
`;

const Card = styled.div`
  position: relative;
  max-width: 520px;
  width: 100%;
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;
`;

const Glow = styled.div`
  position: absolute;
  top: -80px;
  right: -80px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(206, 86, 176, 0.15) 0%, transparent 70%);
  pointer-events: none;
`;

const IconWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: #fdf4fa;
  border: 1px solid rgba(206, 86, 176, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 9999px;
  background: #fdf2f8;
  color: #c94daa;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #171717;
  margin: 0 0 10px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #737373;
  line-height: 1.5;
  margin: 0 0 20px;
`;

const PermissionTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #525252;
  background: #f5f5f5;
  padding: 6px 12px;
  border-radius: 8px;
  margin-bottom: 8px;

  code {
    font-family: monospace;
    color: #c94daa;
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  width: 100%;
  justify-content: center;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  background: #fff;
  color: #171717;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 12px;
  border: none;
  background: ${gradient};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(206, 86, 176, 0.25);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(206, 86, 176, 0.35);
  }
`;
